/**
 * AdminReportForm
 * Self-contained form for admin to report on behalf of a subcontractor.
 * Includes article inputs, image uploads, draft saving, and submission.
 */

import { useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Camera,
  Loader2,
  Save,
  Send,
  X,
  Receipt,
  Plus,
  Trash2,
  Pencil,
  CheckCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { logAssignmentChange, getStatusLabel } from '../../services/activityLog'

// Types
export interface ReportImage {
  url: string
  name: string
  uploaded_at: string
  type: 'before' | 'after' | 'other'
}

export interface ReportedArticle {
  article_id: string | null  // null för utlägg
  article_name: string
  article_description?: string | null
  planned_quantity: number | null
  actual_quantity: number | null
  unit: string | null
  notes: string
  is_extra: boolean
  images: ReportImage[]
  // Utlägg/kvitto-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

interface PlannedArticle {
  articleId: string
  quantity: number
}

interface ExistingDraft {
  reportText: string | null
  reportedArticles: ReportedArticle[] | null
  reportImages: ReportImage[] | null
  isDraft: boolean
}

interface ArticleDefinition {
  id: string
  name: string
  description?: string | null
  unit?: string | null
}

interface AdminReportFormProps {
  assignmentId: string
  reportId: string
  subcontractorId: string
  subcontractorName: string
  plannedArticles: PlannedArticle[]
  articles: ArticleDefinition[]
  existingDraft?: ExistingDraft | null
  adminFeedback?: string | null
  currentAssignmentStatus: string
  onComplete: () => void
  onCancel: () => void
}

export function AdminReportForm({
  assignmentId,
  reportId,
  subcontractorId: _subcontractorId,
  subcontractorName,
  plannedArticles,
  articles,
  existingDraft,
  adminFeedback,
  currentAssignmentStatus,
  onComplete,
  onCancel
}: AdminReportFormProps) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Initialize article reports from existing draft or planned articles
  const initializeArticleReports = (): { articles: ReportedArticle[], expenses: ReportedArticle[] } => {
    if (existingDraft?.reportedArticles && existingDraft.reportedArticles.length > 0) {
      const regularArticles = existingDraft.reportedArticles.filter(a => !a.is_expense)
      const existingExpenses = existingDraft.reportedArticles.filter(a => a.is_expense)
      return { articles: regularArticles, expenses: existingExpenses }
    }
    const regularArticles = plannedArticles.map(pa => {
      const article = articles.find(a => a.id === pa.articleId)
      return {
        article_id: pa.articleId,
        article_name: article?.name || 'Okänd artikel',
        article_description: article?.description || null,
        planned_quantity: pa.quantity,
        actual_quantity: null,
        unit: article?.unit || null,
        notes: '',
        is_extra: false,
        images: []
      }
    })
    return { articles: regularArticles, expenses: [] }
  }

  const initialData = initializeArticleReports()

  // Internal state
  const [articleReports, setArticleReports] = useState<ReportedArticle[]>(initialData.articles)
  const [expenses, setExpenses] = useState<ReportedArticle[]>(initialData.expenses)
  const [reportText, setReportText] = useState(existingDraft?.reportText || '')
  const [reportImages, setReportImages] = useState<ReportImage[]>(existingDraft?.reportImages || [])
  const [savingDraft, setSavingDraft] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingImage, setUploadingImage] = useState<string | null>(null)
  const [activeArticleUpload, setActiveArticleUpload] = useState<number | null>(null)
  const [activeUploadType, setActiveUploadType] = useState<'before' | 'after' | 'other'>('other')
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [editingExpenseIndex, setEditingExpenseIndex] = useState<number | null>(null)
  const [newExpense, setNewExpense] = useState({
    description: '',
    amountExclVat: '',
    amountInclVat: '',
    vatRate: 25,
    lastEdited: 'excl' as 'excl' | 'incl',
    images: [] as ReportImage[]
  })
  const [uploadingExpenseImage, setUploadingExpenseImage] = useState(false)

  // File input refs
  const beforeArticleInputRef = useRef<HTMLInputElement>(null)
  const afterArticleInputRef = useRef<HTMLInputElement>(null)
  const otherArticleInputRef = useRef<HTMLInputElement>(null)
  const beforeGeneralInputRef = useRef<HTMLInputElement>(null)
  const afterGeneralInputRef = useRef<HTMLInputElement>(null)
  const otherGeneralInputRef = useRef<HTMLInputElement>(null)
  const expenseImageInputRef = useRef<HTMLInputElement>(null)

  // Upload a single image to Supabase storage
  const uploadImage = async (
    file: File,
    folder: string,
    type: 'before' | 'after' | 'other'
  ): Promise<ReportImage | null> => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${assignmentId}/${folder}/${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('report-images')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('report-images')
        .getPublicUrl(fileName)

      return {
        url: urlData.publicUrl,
        name: file.name,
        uploaded_at: new Date().toISOString(),
        type
      }
    } catch (err) {
      console.error('Upload error:', err)
      return null
    }
  }

  // Handle article image upload
  const handleArticleImageUpload = async (
    files: FileList | null,
    articleIndex: number,
    type: 'before' | 'after' | 'other'
  ) => {
    if (!files || files.length === 0) return

    const article = articleReports[articleIndex]
    setUploadingImage(article.article_id)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, `article-${article.article_id}`, type)
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      const newReports = [...articleReports]
      newReports[articleIndex] = {
        ...newReports[articleIndex],
        images: [...(newReports[articleIndex].images || []), ...newImages]
      }
      setArticleReports(newReports)
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingImage(null)
    setActiveArticleUpload(null)
  }

  // Handle general report image upload
  const handleGeneralImageUpload = async (
    files: FileList | null,
    type: 'before' | 'after' | 'other'
  ) => {
    if (!files || files.length === 0) return

    setUploadingImage('general')
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, 'general', type)
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setReportImages([...reportImages, ...newImages])
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingImage(null)
  }

  // Handle expense image upload
  const handleExpenseImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setUploadingExpenseImage(true)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, 'expense', 'other')
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setNewExpense(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }))
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingExpenseImage(false)
  }

  const removeArticleImage = (articleIndex: number, imageIndex: number) => {
    const newReports = [...articleReports]
    newReports[articleIndex] = {
      ...newReports[articleIndex],
      images: newReports[articleIndex].images.filter((_, i) => i !== imageIndex)
    }
    setArticleReports(newReports)
  }

  const removeGeneralImage = (index: number) => {
    setReportImages(reportImages.filter((_, i) => i !== index))
  }

  // Save as draft
  const handleSaveDraft = async () => {
    setSavingDraft(true)
    try {
      // Combine articles and expenses
      const allReportedArticles = [...articleReports, ...expenses]

      await supabase
        .from('assignment_subcontractors')
        .update({
          report_text: reportText || null,
          reported_articles: allReportedArticles,
          report_images: reportImages,
          is_draft: true,
          draft_saved_at: new Date().toISOString(),
          draft_saved_by: user?.id
        })
        .eq('id', reportId)

      toast.success('Utkast sparat')
      queryClient.invalidateQueries({ queryKey: ['assignment-reports', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      onComplete()
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte spara utkast')
    } finally {
      setSavingDraft(false)
    }
  }

  // Submit report
  const handleSubmit = async () => {
    // Validate
    const hasArticleReports = articleReports.some(a => a.actual_quantity !== null && a.actual_quantity > 0)
    const hasExpenses = expenses.length > 0
    if (!hasArticleReports && !hasExpenses && !reportText.trim()) {
      toast.error('Rapportera minst en artikel, ett utlägg eller skriv en anteckning')
      return
    }

    setSubmitting(true)
    try {
      const now = new Date().toISOString()

      // Combine articles and expenses
      const allReportedArticles = [...articleReports, ...expenses]

      // Save report
      await supabase
        .from('assignment_subcontractors')
        .update({
          report_text: reportText || null,
          reported_at: now,
          reported_articles: allReportedArticles,
          report_images: reportImages,
          is_draft: false,
          tg_status: 'rapporterad'
        })
        .eq('id', reportId)

      // Check if all subcontractors have now reported (using tg_status for V2)
      const { data: allLinks } = await supabase
        .from('assignment_subcontractors')
        .select('id, tg_status')
        .eq('assignment_id', assignmentId)

      // All TGs must be rapporterad, godkand, or avsagt - but at least one must be rapporterad/godkand
      const completedStatuses = ['rapporterad', 'godkand', 'avsagt']
      const allComplete = allLinks && allLinks.length > 0 &&
        allLinks.every(link => link.id === reportId || completedStatuses.includes(link.tg_status))
      const hasActualReport = allLinks && allLinks.some(link =>
        link.id === reportId || link.tg_status === 'rapporterad' || link.tg_status === 'godkand'
      )

      if (allComplete && hasActualReport && currentAssignmentStatus !== 'granskning') {
        await supabase
          .from('assignments')
          .update({
            status: 'granskning',
            updated_at: now
          })
          .eq('id', assignmentId)

        await logAssignmentChange(assignmentId, 'status_changed', {
          fieldName: 'status',
          oldValue: getStatusLabel(currentAssignmentStatus),
          newValue: getStatusLabel('granskning'),
          description: 'Alla trädgårdsmästare har rapporterat'
        })
      }

      // Log report submission
      const reportedArticlesSummary = articleReports
        .filter(a => a.actual_quantity !== null && a.actual_quantity > 0)
        .map(a => `${a.article_name}: ${a.actual_quantity} ${a.unit || 'st'}`)
        .join(', ')

      await logAssignmentChange(assignmentId, 'report_submitted', {
        description: `Admin rapporterade åt ${subcontractorName}${reportedArticlesSummary ? `: ${reportedArticlesSummary}` : ''}`
      })

      toast.success('Rapport sparad')
      queryClient.invalidateQueries({ queryKey: ['assignment-reports', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', assignmentId] })
      onComplete()
    } catch (err) {
      console.error(err)
      toast.error('Kunde inte spara')
    } finally {
      setSubmitting(false)
    }
  }

  const isProcessing = savingDraft || submitting

  return (
    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
      <label className="block text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
        Rapportera åt {subcontractorName}:
      </label>
      <p className="text-xs text-blue-600 dark:text-blue-300 mb-3">
        <strong>Spara utkast</strong> – sparar rapporten så du kan fortsätta senare.<br />
        <strong>Skicka in rapport</strong> – skickar in rapporten för godkännande.
      </p>

      {/* Show admin feedback/reason if any */}
      {adminFeedback && (
        <div className={`mb-3 p-3 rounded border ${
          adminFeedback.startsWith('[MANUELL]')
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}>
          <p className={`text-sm font-medium mb-1 ${
            adminFeedback.startsWith('[MANUELL]')
              ? 'text-blue-800 dark:text-blue-200'
              : 'text-amber-800 dark:text-amber-200'
          }`}>
            {adminFeedback.startsWith('[MANUELL]')
              ? '🔧 Manuell komplettering'
              : '📤 Begärd komplettering från trädgårdsmästare'}
          </p>
          <p className={`text-sm ${
            adminFeedback.startsWith('[MANUELL]')
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-amber-700 dark:text-amber-300'
          }`}>
            {adminFeedback.replace(/^\[(MANUELL|BEGÄRD)\]\s*/, '')}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {/* Article inputs */}
        {articleReports.length > 0 && (
          <div>
            <label className="block text-xs text-blue-700 dark:text-blue-300 mb-2">Artiklar</label>
            <div className="space-y-2">
              {articleReports.map((article, index) => (
                <div
                  key={`${article.article_id}-${index}`}
                  className={`p-3 rounded border ${
                    article.is_extra
                      ? 'bg-blue-100 dark:bg-blue-800/30 border-blue-300 dark:border-blue-700'
                      : 'bg-white dark:bg-nature-800 border-nature-200 dark:border-nature-600'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                        {article.article_name}
                      </span>
                      {article.is_extra && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-200 rounded">
                          Extra
                        </span>
                      )}
                      {article.article_description && (
                        <p className="text-xs text-nature-500 dark:text-nature-400 mt-0.5">
                          {article.article_description}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          defaultValue={article.actual_quantity !== null ? String(article.actual_quantity).replace('.', ',') : ''}
                          key={`${article.article_id}-${article.actual_quantity}`}
                          onBlur={(e) => {
                            const input = e.target.value.replace(',', '.')
                            if (input === '') {
                              const newReports = [...articleReports]
                              newReports[index] = { ...newReports[index], actual_quantity: null }
                              setArticleReports(newReports)
                            } else {
                              const parsed = parseFloat(input)
                              if (!isNaN(parsed)) {
                                const newReports = [...articleReports]
                                newReports[index] = { ...newReports[index], actual_quantity: parsed }
                                setArticleReports(newReports)
                              }
                            }
                          }}
                          className="w-20 px-2 py-1 text-sm text-center border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700"
                          placeholder="0"
                        />
                        <span className="text-xs text-nature-500 w-10">{article.unit || 'st'}</span>
                      </div>
                      {article.planned_quantity !== null && (
                        <button
                          type="button"
                          onClick={() => {
                            const newReports = [...articleReports]
                            newReports[index] = {
                              ...newReports[index],
                              actual_quantity: article.planned_quantity
                            }
                            setArticleReports(newReports)
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700 rounded"
                        >
                          Använd planerat ({article.planned_quantity})
                        </button>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={article.notes}
                    onChange={(e) => {
                      const newReports = [...articleReports]
                      newReports[index] = {
                        ...newReports[index],
                        notes: e.target.value
                      }
                      setArticleReports(newReports)
                    }}
                    className="mt-2 w-full px-2 py-1 text-xs border border-nature-200 dark:border-nature-600 rounded bg-white dark:bg-nature-700"
                    placeholder="Anteckning (valfritt)..."
                  />

                  {/* Article images */}
                  <div className="mt-2 space-y-2">
                    {/* Before images */}
                    {article.images?.filter(img => img.type === 'before').length > 0 && (
                      <div>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Före:</p>
                        <div className="flex flex-wrap gap-1">
                          {article.images.filter(img => img.type === 'before').map((img, imgIdx) => {
                            const actualIdx = article.images.findIndex(i => i === img)
                            return (
                              <div key={imgIdx} className="relative group">
                                <img
                                  src={img.url}
                                  alt={img.name}
                                  className="w-10 h-10 object-cover rounded border-2 border-amber-400 dark:border-amber-500"
                                />
                                <button
                                  onClick={() => removeArticleImage(index, actualIdx)}
                                  className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {/* After images */}
                    {article.images?.filter(img => img.type === 'after').length > 0 && (
                      <div>
                        <p className="text-xs text-green-600 dark:text-green-400 mb-1">Efter:</p>
                        <div className="flex flex-wrap gap-1">
                          {article.images.filter(img => img.type === 'after').map((img, imgIdx) => {
                            const actualIdx = article.images.findIndex(i => i === img)
                            return (
                              <div key={imgIdx} className="relative group">
                                <img
                                  src={img.url}
                                  alt={img.name}
                                  className="w-10 h-10 object-cover rounded border-2 border-green-400 dark:border-green-500"
                                />
                                <button
                                  onClick={() => removeArticleImage(index, actualIdx)}
                                  className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {/* Other images */}
                    {article.images?.filter(img => img.type === 'other').length > 0 && (
                      <div>
                        <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">Övriga:</p>
                        <div className="flex flex-wrap gap-1">
                          {article.images.filter(img => img.type === 'other').map((img, imgIdx) => {
                            const actualIdx = article.images.findIndex(i => i === img)
                            return (
                              <div key={imgIdx} className="relative group">
                                <img
                                  src={img.url}
                                  alt={img.name}
                                  className="w-10 h-10 object-cover rounded border border-nature-300 dark:border-nature-500"
                                />
                                <button
                                  onClick={() => removeArticleImage(index, actualIdx)}
                                  className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {/* Upload buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          setActiveArticleUpload(index)
                          setActiveUploadType('before')
                          beforeArticleInputRef.current?.click()
                        }}
                        disabled={uploadingImage === article.article_id}
                        className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 flex items-center gap-1"
                      >
                        {uploadingImage === article.article_id && activeUploadType === 'before' ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Camera className="h-3 w-3" />
                        )}
                        Före
                      </button>
                      <button
                        onClick={() => {
                          setActiveArticleUpload(index)
                          setActiveUploadType('after')
                          afterArticleInputRef.current?.click()
                        }}
                        disabled={uploadingImage === article.article_id}
                        className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 flex items-center gap-1"
                      >
                        {uploadingImage === article.article_id && activeUploadType === 'after' ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Camera className="h-3 w-3" />
                        )}
                        Efter
                      </button>
                      <button
                        onClick={() => {
                          setActiveArticleUpload(index)
                          setActiveUploadType('other')
                          otherArticleInputRef.current?.click()
                        }}
                        disabled={uploadingImage === article.article_id}
                        className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 rounded hover:bg-nature-200 flex items-center gap-1"
                      >
                        {uploadingImage === article.article_id && activeUploadType === 'other' ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Camera className="h-3 w-3" />
                        )}
                        Övrig
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Hidden inputs for article images */}
            <input
              ref={beforeArticleInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (activeArticleUpload !== null) {
                  handleArticleImageUpload(e.target.files, activeArticleUpload, 'before')
                }
                e.target.value = ''
              }}
              className="hidden"
            />
            <input
              ref={afterArticleInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (activeArticleUpload !== null) {
                  handleArticleImageUpload(e.target.files, activeArticleUpload, 'after')
                }
                e.target.value = ''
              }}
              className="hidden"
            />
            <input
              ref={otherArticleInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (activeArticleUpload !== null) {
                  handleArticleImageUpload(e.target.files, activeArticleUpload, 'other')
                }
                e.target.value = ''
              }}
              className="hidden"
            />
          </div>
        )}

        {/* General report images */}
        <div>
          <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">Övriga bilder</label>
          <div className="space-y-2">
            {/* Before images */}
            {reportImages.filter(img => img.type === 'before').length > 0 && (
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Före:</p>
                <div className="flex flex-wrap gap-2">
                  {reportImages.filter(img => img.type === 'before').map((img, idx) => {
                    const actualIdx = reportImages.findIndex(i => i === img)
                    return (
                      <div key={idx} className="relative group">
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-14 h-14 object-cover rounded border-2 border-amber-400 dark:border-amber-500"
                        />
                        <button
                          onClick={() => removeGeneralImage(actualIdx)}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {/* After images */}
            {reportImages.filter(img => img.type === 'after').length > 0 && (
              <div>
                <p className="text-xs text-green-600 dark:text-green-400 mb-1">Efter:</p>
                <div className="flex flex-wrap gap-2">
                  {reportImages.filter(img => img.type === 'after').map((img, idx) => {
                    const actualIdx = reportImages.findIndex(i => i === img)
                    return (
                      <div key={idx} className="relative group">
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-14 h-14 object-cover rounded border-2 border-green-400 dark:border-green-500"
                        />
                        <button
                          onClick={() => removeGeneralImage(actualIdx)}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {/* Other images */}
            {reportImages.filter(img => img.type === 'other').length > 0 && (
              <div>
                <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">Övriga:</p>
                <div className="flex flex-wrap gap-2">
                  {reportImages.filter(img => img.type === 'other').map((img, idx) => {
                    const actualIdx = reportImages.findIndex(i => i === img)
                    return (
                      <div key={idx} className="relative group">
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-14 h-14 object-cover rounded border border-nature-300 dark:border-nature-500"
                        />
                        <button
                          onClick={() => removeGeneralImage(actualIdx)}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {/* Hidden inputs */}
            <input
              ref={beforeGeneralInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                handleGeneralImageUpload(e.target.files, 'before')
                e.target.value = ''
              }}
              className="hidden"
            />
            <input
              ref={afterGeneralInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                handleGeneralImageUpload(e.target.files, 'after')
                e.target.value = ''
              }}
              className="hidden"
            />
            <input
              ref={otherGeneralInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                handleGeneralImageUpload(e.target.files, 'other')
                e.target.value = ''
              }}
              className="hidden"
            />
            {/* Upload buttons */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => beforeGeneralInputRef.current?.click()}
                disabled={uploadingImage === 'general'}
                className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 flex items-center gap-1"
              >
                {uploadingImage === 'general' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Camera className="h-3 w-3" />
                )}
                Före-bild
              </button>
              <button
                onClick={() => afterGeneralInputRef.current?.click()}
                disabled={uploadingImage === 'general'}
                className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 flex items-center gap-1"
              >
                {uploadingImage === 'general' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Camera className="h-3 w-3" />
                )}
                Efter-bild
              </button>
              <button
                onClick={() => otherGeneralInputRef.current?.click()}
                disabled={uploadingImage === 'general'}
                className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 rounded hover:bg-nature-200 flex items-center gap-1"
              >
                {uploadingImage === 'general' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Camera className="h-3 w-3" />
                )}
                Övrig bild
              </button>
            </div>
          </div>
        </div>

        {/* Expenses section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
              <Receipt className="h-3 w-3" />
              Utlägg & Kvitton
            </label>
            <button
              type="button"
              onClick={() => setShowExpenseModal(true)}
              className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Lägg till utlägg
            </button>
          </div>

          {expenses.length > 0 ? (
            <div className="space-y-2">
              {expenses.map((expense, index) => (
                <div
                  key={`expense-${index}`}
                  className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                          {expense.article_name}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-nature-600 dark:text-nature-400">
                        <span className="font-medium">{expense.expense_amount_incl_vat} kr</span>
                        <span className="text-nature-500"> inkl moms</span>
                        <span className="mx-1">•</span>
                        <span>{expense.expense_amount_excl_vat?.toFixed(2)} kr ex moms</span>
                        <span className="mx-1">•</span>
                        <span>{expense.expense_vat_rate}% moms</span>
                      </div>
                      {expense.notes && (
                        <p className="mt-1 text-xs text-nature-500 dark:text-nature-400">
                          {expense.notes}
                        </p>
                      )}
                      {/* Expense images */}
                      {expense.images && expense.images.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {expense.images.map((img, imgIdx) => (
                            <a
                              key={imgIdx}
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={img.url}
                                alt={img.name}
                                className="w-10 h-10 object-cover rounded border border-amber-300 dark:border-amber-600 hover:border-amber-500"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingExpenseIndex(index)
                          setNewExpense({
                            description: expense.article_name,
                            amountExclVat: String(expense.expense_amount_excl_vat || ''),
                            amountInclVat: String(expense.expense_amount_incl_vat || ''),
                            vatRate: expense.expense_vat_rate || 25,
                            lastEdited: 'excl',
                            images: expense.images || []
                          })
                          setShowExpenseModal(true)
                        }}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                        title="Redigera utlägg"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setExpenses(expenses.filter((_, i) => i !== index))
                        }}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                        title="Ta bort utlägg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-nature-500 dark:text-nature-400 italic">
              Inga utlägg registrerade. Klicka på "Lägg till utlägg" för att lägga till kvitton och egna utlägg.
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">Övriga anteckningar</label>
          <textarea
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
            rows={3}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          {/* Save Draft Button */}
          <button
            onClick={handleSaveDraft}
            disabled={isProcessing}
            className="px-3 py-1.5 text-sm bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200 hover:bg-nature-300 dark:hover:bg-nature-500 rounded flex items-center gap-1"
          >
            {savingDraft ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Spara utkast
          </button>

          {/* Submit Report Button */}
          <button
            onClick={handleSubmit}
            disabled={isProcessing}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Skicka in rapport
          </button>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="px-3 py-1.5 text-sm bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600 rounded"
          >
            Avbryt
          </button>
        </div>
      </div>

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 flex items-center gap-2">
                {editingExpenseIndex !== null ? (
                  <Pencil className="h-5 w-5 text-amber-600" />
                ) : (
                  <Receipt className="h-5 w-5 text-amber-600" />
                )}
                {editingExpenseIndex !== null ? 'Redigera utlägg' : 'Lägg till utlägg'}
              </h3>
              <button
                onClick={() => {
                  setShowExpenseModal(false)
                  setEditingExpenseIndex(null)
                  setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
                }}
                className="p-1 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Beskrivning *
                </label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="T.ex. Parkering, Material..."
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                />
              </div>

              {/* VAT rate */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Momssats
                </label>
                <select
                  value={newExpense.vatRate}
                  onChange={(e) => {
                    const newRate = Number(e.target.value)
                    if (newExpense.lastEdited === 'incl' && newExpense.amountInclVat) {
                      const incl = parseFloat(newExpense.amountInclVat.replace(',', '.'))
                      if (!isNaN(incl)) {
                        const excl = newRate > 0 ? incl / (1 + newRate / 100) : incl
                        setNewExpense({ ...newExpense, vatRate: newRate, amountExclVat: excl.toFixed(2) })
                        return
                      }
                    }
                    if (newExpense.amountExclVat) {
                      const excl = parseFloat(newExpense.amountExclVat.replace(',', '.'))
                      if (!isNaN(excl)) {
                        const incl = excl * (1 + newRate / 100)
                        setNewExpense({ ...newExpense, vatRate: newRate, amountInclVat: incl.toFixed(2) })
                        return
                      }
                    }
                    setNewExpense({ ...newExpense, vatRate: newRate })
                  }}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                >
                  <option value={25}>25%</option>
                  <option value={12}>12%</option>
                  <option value={6}>6%</option>
                  <option value={0}>0% (momsfritt)</option>
                </select>
              </div>

              {/* Amount excl VAT */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Belopp ex moms (kr) *
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountExclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const inclVal = !isNaN(parsed) && val ? (parsed * (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense({ ...newExpense, amountExclVat: val, amountInclVat: inclVal, lastEdited: 'excl' })
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                />
              </div>

              {/* Amount incl VAT */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Belopp inkl moms (kr)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountInclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const exclVal = !isNaN(parsed) && val ? (parsed / (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense({ ...newExpense, amountInclVat: val, amountExclVat: exclVal, lastEdited: 'incl' })
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
                />
              </div>

              {/* Receipt images */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Kvittobild (valfritt)
                </label>
                <div className="space-y-2">
                  {/* Show uploaded images */}
                  {newExpense.images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newExpense.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-14 h-14 object-cover rounded border border-amber-300 dark:border-amber-600"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setNewExpense(prev => ({
                                ...prev,
                                images: prev.images.filter((_, i) => i !== idx)
                              }))
                            }}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={() => expenseImageInputRef.current?.click()}
                    disabled={uploadingExpenseImage}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50"
                  >
                    {uploadingExpenseImage ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Camera className="h-3 w-3" />
                    )}
                    {uploadingExpenseImage ? 'Laddar upp...' : 'Lägg till kvittobild'}
                  </button>
                  <input
                    ref={expenseImageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      handleExpenseImageUpload(e.target.files)
                      e.target.value = ''
                    }}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    // Validate
                    if (!newExpense.description.trim()) {
                      toast.error('Ange en beskrivning')
                      return
                    }
                    const amountExclVat = parseFloat(newExpense.amountExclVat.replace(',', '.'))
                    if (isNaN(amountExclVat) || amountExclVat <= 0) {
                      toast.error('Ange ett giltigt belopp')
                      return
                    }

                    // Calculate incl VAT
                    const amountInclVat = amountExclVat * (1 + newExpense.vatRate / 100)

                    // Create expense entry
                    const expense: ReportedArticle = {
                      article_id: null,
                      article_name: newExpense.description.trim(),
                      article_description: null,
                      planned_quantity: null,
                      actual_quantity: 1,
                      unit: 'st',
                      notes: '',
                      is_extra: false,
                      images: newExpense.images,
                      is_expense: true,
                      expense_amount_incl_vat: Math.round(amountInclVat * 100) / 100,
                      expense_amount_excl_vat: amountExclVat,
                      expense_vat_rate: newExpense.vatRate
                    }

                    if (editingExpenseIndex !== null) {
                      // Update existing expense
                      const updatedExpenses = [...expenses]
                      updatedExpenses[editingExpenseIndex] = expense
                      setExpenses(updatedExpenses)
                      toast.success('Utlägg uppdaterat')
                    } else {
                      // Add new expense
                      setExpenses([...expenses, expense])
                      toast.success('Utlägg tillagt')
                    }
                    setShowExpenseModal(false)
                    setEditingExpenseIndex(null)
                    setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium flex items-center justify-center gap-2"
                >
                  {editingExpenseIndex !== null ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Spara
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Lägg till
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowExpenseModal(false)
                    setEditingExpenseIndex(null)
                    setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
                  }}
                  className="px-4 py-2 bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200 hover:bg-nature-300 dark:hover:bg-nature-500 rounded"
                >
                  Avbryt
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
